import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Keycloak from 'keycloak-js';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private keycloakInstance: Keycloak | null = null;
  private _isInitialized = false;
  private _isAuthenticated = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /** Initialise Keycloak (appelé par APP_INITIALIZER) */
  async init(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      // console.warn('[KeycloakAuth] Initialisation ignorée (SSR)');
      return false;
    }

    if (this._isInitialized) return this._isAuthenticated;

    this.keycloakInstance = new Keycloak({
      url: environment.keycloak.url,
      realm: environment.keycloak.realm,
      clientId: environment.keycloak.clientId,
    });

    try {
      const authenticated = await this.keycloakInstance.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      });
      this._isAuthenticated = authenticated;
      this._isInitialized = true;
      // console.log('[KeycloakAuth] Initialisé, authentifié:', authenticated);
      return authenticated;
    } catch (error) {
      console.error('[KeycloakAuth] Erreur init:', error);
      this._isInitialized = true;
      this._isAuthenticated = false;
      return false;
    }
  }

  getInstance(): Keycloak | null {
    return this.keycloakInstance;
  }

  async isLoggedIn(): Promise<boolean> {
    if (!this.keycloakInstance) return false;
    return !!this.keycloakInstance.authenticated;
  }

  async updateToken(minValidity = 5): Promise<boolean> {
    if (!this.keycloakInstance) return false;
    try {
      const refreshed = await this.keycloakInstance.updateToken(minValidity);
      if (refreshed) console.log('[KeycloakAuth] Token rafraîchi');
      return true;
    } catch (error) {
      console.error('[KeycloakAuth] Échec rafraîchissement token', error);
      return false;
    }
  }

  async getToken(): Promise<string | undefined> {
    if (!this.keycloakInstance) return undefined;
    await this.updateToken();
    return this.keycloakInstance.token;
  }

  /** Charge le profil utilisateur et retourne les données */
  async loadUserProfile(): Promise<any> {
    if (!this.keycloakInstance) return null;
    try {
      const profile = await this.keycloakInstance.loadUserInfo();
      return profile;
    } catch (error) {
      console.error('[KeycloakAuth] Erreur chargement profil', error);
      return null;
    }
  }

  getUserRoles(): string[] {
    if (!this.keycloakInstance || !this.keycloakInstance.tokenParsed) return [];
    const tokenParsed = this.keycloakInstance.tokenParsed;
    const realmRoles = tokenParsed.realm_access?.roles || [];
    const resourceAccess = tokenParsed.resource_access || {};
    const clientRoles: string[] = [];
    Object.keys(resourceAccess).forEach(clientId => {
      const roles = resourceAccess[clientId]?.roles || [];
      clientRoles.push(...roles);
    });
    return [...new Set([...realmRoles, ...clientRoles])];
  }

  getResourceAccessKeys(): string[] {
    if (!this.keycloakInstance || !this.keycloakInstance.tokenParsed) return [];
    const resourceAccess = this.keycloakInstance.tokenParsed.resource_access;
    return resourceAccess ? Object.keys(resourceAccess) : [];
  }

  logout(redirectUri?: string): void {
    if (this.keycloakInstance) {
      this.keycloakInstance.logout({ redirectUri: redirectUri || window.location.origin });
    }
  }

  login(options?: Keycloak.KeycloakLoginOptions): void {
    this.keycloakInstance?.login(options);
  }
}